import {
  Control,
  Controller,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import { ReportFormValues } from '../reports-form-page';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { SectionHeader } from '../../../../shared/section-header';
import UserAvatar from '../../../../shared/user-avatar';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Clear from '@mui/icons-material/Clear';

interface StepShareNotifyProps {
  control: Control<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
  watch: UseFormWatch<ReportFormValues>;
  loggedInUser: string;
}

export default function StepShareNotify({
  control,
  setValue,
  watch,
  loggedInUser,
}: StepShareNotifyProps) {
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [autoCompleteShareResponse, setAutoCompleteShareResponse] =
    useState('');
  const [autoCompleteShareKey, setAutocompleteShareKey] = useState(
    new Date().getTime().toString()
  );
  const emailsToNotify = watch('emailsToNotify') || [];
  const _notifyViaEmail = watch('_notifyViaEmail');
  const reportName = watch('reportName') || '';

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'permissions',
  });

  return (
    <Stack
      sx={{
        viewTransitionName: 'steps',
      }}
      gap={2}
      width="100%"
      height="100%"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SectionHeader
          description="Optionally provide any specific users that you would like to send an
          email containing a link to the report upon this reports completion."
        >
          Emails
        </SectionHeader>
        <Stack gap={1}>
          <Stack flexDirection="row" gap={1}>
            <Controller
              name="emailsToNotify"
              control={control}
              render={() => (
                <Box sx={{ width: 400 }}>
                  <UsersAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      setAutoCompleteResponse(value);
                      if (!_notifyViaEmail) {
                        setValue('_notifyViaEmail', true);
                      }
                      if (value && !emailsToNotify.includes(value)) {
                        setValue('emailsToNotify', [...emailsToNotify, value]);
                      }
                      autocompleteKey.current = new Date().getTime().toString();
                      setAutoCompleteResponse('');
                    }}
                  />
                </Box>
              )}
            />
          </Stack>
          <Stack
            flexDirection="row"
            gap={1}
            flexWrap="wrap"
            sx={{
              mt: 1,
              minHeight: 70,
              maxHeight: 70,
              overflowY: 'auto',
            }}
          >
            {emailsToNotify.map((user) => (
              <AvatarChip
                key={user}
                label={user}
                type="User"
                email={user}
                onDelete={() => {
                  setValue(
                    'emailsToNotify',
                    emailsToNotify.filter(
                      (notifiedUser) => notifiedUser !== user
                    )
                  );
                  if (_notifyViaEmail && emailsToNotify.length === 0) {
                    setValue('_notifyViaEmail', false);
                  }
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          gap: 2,
        }}
      >
        <SectionHeader
          description="Optionally provide any specific users that you would like to give
          Share access to the reports file and the access role that they would
          be assigned."
        >
          Share
        </SectionHeader>
        <Stack flexDirection="row" gap={2} sx={{ mb: 0.5 }}>
          <Box sx={{ width: 542 }}>
            <SharedAutocomplete
              sources={['users']}
              autocompleteValue={autoCompleteShareResponse}
              setAutoCompleteValue={(value) => {
                setAutoCompleteShareResponse(value);
                if (fields.find((perm) => perm.userEmail === value)) return;
                if (value === '') return;
                append({
                  userEmail: value,
                  role: 'reader',
                  type: 'user',
                  withLink: false,
                  sendNotification: false,
                  transferOwnership: false,
                  additionalRoles: [],
                  isCommenter: false,
                  photoLink: '',
                  docId: '',
                  mimeType: 'application/vnd.google-apps.drive',
                  isPrimaryOwner: false,
                  toBeDeleted: false,
                  permExpirationTime: 0,
                  permId: '',
                  docOwner: loggedInUser,
                  docTitle: reportName,
                  allowFileDiscovery: false,
                  displayName: '',
                  permissionDetails: [],
                });
                setAutoCompleteShareResponse('');
                setAutocompleteShareKey(new Date().getTime().toString());
              }}
              key={autoCompleteShareKey}
            />
          </Box>
        </Stack>
        <List sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
          {fields.map((perm, index) => (
            <Stack key={perm.userEmail}>
              <ListItem
                dense
                sx={{ maxWidth: 600 }}
                secondaryAction={
                  <Stack flexDirection="row" gap={1} alignItems="center">
                    <Controller
                      name={`permissions.${index}.role`}
                      control={control}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                            inputProps={{
                              id: `role-${index}`,
                            }}
                            InputLabelProps={{
                              htmlFor: `role-${index}`,
                            }}
                            select
                            defaultValue={perm.role}
                            label="Role"
                            size="small"
                            sx={{ width: 175 }}
                          >
                            <MenuItem key="reader" value="reader">
                              Viewer
                            </MenuItem>
                            <MenuItem key="commenter" value="commenter">
                              Commenter
                            </MenuItem>
                            <MenuItem key="writer" value="writer">
                              Editor
                            </MenuItem>
                          </TextField>
                          <Tooltip arrow title="Remove">
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    />
                  </Stack>
                }
              >
                <ListItemAvatar>
                  <UserAvatar userEmail={perm.userEmail || ''} />
                </ListItemAvatar>
                <ListItemText
                  primary={perm.userEmail}
                  primaryTypographyProps={{
                    maxWidth: 200,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                />
              </ListItem>
            </Stack>
          ))}
        </List>
      </Box>
    </Stack>
  );
}
